import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ConfettiBurst from '../common/ConfettiBurst';
import type { ItemCard } from '../../types/pointItemNote';
import { resolveItemCardImageSrc } from '../../utils/itemCardImage';
import ItemCardStackIcon from './ItemCardStackIcon';

type Props = {
  open: boolean;
  cards: ItemCard[];
  mode: 'grant' | 'unlock';
  onClose: () => void;
};

const SPIN_MS = 900;

const ClassPointClaimReveal: React.FC<Props> = ({ open, cards, mode, onClose }) => {
  const [phase, setPhase] = useState<'spinning' | 'reveal'>('spinning');
  const [revealKey, setRevealKey] = useState(0);

  useEffect(() => {
    if (!open) return;
    setPhase('spinning');
    setRevealKey(Date.now());
    const t1 = window.setTimeout(() => setPhase('reveal'), SPIN_MS);
    return () => {
      window.clearTimeout(t1);
    };
  }, [open, cards]);

  if (!open || cards.length === 0) return null;

  const isSpinning = phase === 'spinning';

  return createPortal(
    <div
      className="cp-pin-profile-draw-reveal cp-pin-class-point-claim-reveal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cp-pin-class-point-claim-title"
    >
      {!isSpinning ? <ConfettiBurst animKey={revealKey} /> : null}
      <div className="cp-pin-class-point-claim-cards">
        {cards.map((card, idx) => {
          const src = resolveItemCardImageSrc(card.cardImage);
          return (
            <div
              key={`${card.itemCardId}-${idx}`}
              className={`cp-pin-profile-draw-reveal-card${
                isSpinning ? ' is-spinning' : ' is-settled'
              }`}
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              <div className="cp-pin-profile-draw-reveal-card-image">
                {src ? <img src={src} alt="" /> : <ItemCardStackIcon size={72} />}
              </div>
              <div className="cp-pin-profile-draw-reveal-card-footer">
                <span className="cp-pin-profile-draw-reveal-card-number">
                  {isSpinning ? '???' : card.name || '보상 카드'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <p
        id="cp-pin-class-point-claim-title"
        className={`cp-pin-profile-draw-reveal-text${isSpinning ? ' is-spinning' : ''}`}
      >
        {isSpinning
          ? '학급 보상을 여는 중…'
          : mode === 'unlock'
            ? '잠금이 해제되었습니다!'
            : '학급 보상을 획득했습니다!'}
      </p>
      {!isSpinning ? (
        <button
          type="button"
          className="cp-pin-class-point-claim-close"
          onClick={onClose}
        >
          닫기
        </button>
      ) : (
        <p className="cp-pin-profile-draw-reveal-hint">{'\u00A0'}</p>
      )}
    </div>,
    document.body
  );
};

export default ClassPointClaimReveal;
